import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButton, ToggleButtonGroup,
  List, ListItem, Divider, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ViewListIcon from '@mui/icons-material/ViewList';
import axios from 'axios';
import ProposalCalendar from './ProposalCalendar';
import {
  getPlanWeekForToday,
  getAllowedMonthStart,
  getAllowedMonthEnd,
  formatPlanWindowDate,
  formatPlanMonthName,
} from './planWeekUtils';
import { getNowInPst, getSubmissionStatus } from './submissionUtils';
import { formatLocalYmd, normalizeCalendarDate } from './formatLocalYmd';

const baseURL = process.env.NODE_ENV === 'production' ? 'https://allcprmanage.com' : 'http://localhost:8080';

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatSlotTime = (t) => {
  if (!t) return '--:--';
  return String(t).trim().slice(0, 5);
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr || dateStr.length < 10) return dateStr || '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dayOfWeek = new Date(y, m - 1, d).getDay();
  return `${MONTH_SHORT[m - 1]} ${String(d).padStart(2, '0')} (${DAY_SHORT[dayOfWeek]})`;
};

const getSlotInstanceKey = (slot) => {
  const date = normalizeCalendarDate(slot?.date) || '';
  const start = String(slot?.startTime || '').trim().slice(0, 5);
  const end = String(slot?.endTime || '').trim().slice(0, 5);
  return `${slot?.id ?? ''}|${date}|${start}|${end}`;
};

/**
 * Instructor public availability page.
 * URL: /calendar/availability?token={base64(instructor_id|date)}
 */
const ProposalCalendarPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [instructorName, setInstructorName] = useState('');
  const [countdown, setCountdown] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState('NO'); // YES | NO | EXPIRED
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 'list' is the default; user can switch to 'calendar'

  const [viewMode, setViewMode] = useState('list');

  // List view data
  const [listSlots, setListSlots] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [checkedSlotIds, setCheckedSlotIds] = useState(new Set()); // Set of slot instance keys

  const planWeek = useMemo(() => getPlanWeekForToday(), []);

  // --- Fetch instructor info ---
  useEffect(() => {
    if (!token) return;
    axios.get(`${baseURL}/proposal/instructor/info`, { params: { token } })
      .then((res) => {
        if (res.data?.code === 0 && res.data?.data) {
          const data = res.data.data;
          const fn = (data.firstname || '').toUpperCase();
          const ln = (data.lastname || '').toUpperCase();
          setInstructorName(`${fn} ${ln}`.trim());
          const subTime = data.submissionTime || data.submission_time || null;
          const nowPst = getNowInPst();
          setSubmissionStatus(getSubmissionStatus(subTime, nowPst));
        }
      })
      .catch(() => {});
  }, [token]);

  // --- Countdown timer ---
  useEffect(() => {
    if (!planWeek.inPlanWeek || !planWeek.windowEnd) return;

    const target = new Date(
      planWeek.windowEnd.getFullYear(),
      planWeek.windowEnd.getMonth(),
      planWeek.windowEnd.getDate(),
      23, 59, 0, 0,
    );

    const updateCountdown = () => {
      const diffMs = target.getTime() - Date.now();
      if (diffMs <= 0) { setCountdown('00d 00h 00m 00s'); return; }
      const totalSeconds = Math.floor(diffMs / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const p = (n) => String(n).padStart(2, '0');
      setCountdown(`${p(days)}d ${p(hours)}h ${p(minutes)}m ${p(seconds)}s`);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [planWeek.inPlanWeek, planWeek.windowEnd]);

  // --- Fetch manager target slots for list view ---
  const fetchListSlots = useCallback(async () => {
    if (!token || !planWeek.inPlanWeek) return;
    setListLoading(true);
    try {
      const monthStart = getAllowedMonthStart(planWeek.allowedYear, planWeek.allowedMonth);
      const monthEnd = getAllowedMonthEnd(planWeek.allowedYear, planWeek.allowedMonth);
      const res = await axios.get(`${baseURL}/proposal/calendar/public`, {
        params: { token, monthStart, monthEnd },
      });
      const allSlots = Array.isArray(res.data?.data) ? res.data.data : [];
      const managerSlots = allSlots.filter((ev) => {
        const sourceType = ev.sourceType || 'INSTRUCTOR';
        const isRejected = ev.isRejected === true || ev.rejected === true;
        const isCancelled = ev.isCancelled === true || ev.cancelled === true;
        return sourceType === 'MANAGER' && !isRejected && !isCancelled;
      });
      managerSlots.sort((a, b) => {
        const da = normalizeCalendarDate(a.date) || '';
        const db = normalizeCalendarDate(b.date) || '';
        if (da !== db) return da < db ? -1 : 1;
        return String(a.startTime || '').localeCompare(String(b.startTime || ''));
      });
      setListSlots(managerSlots);
    } catch (e) {
      console.error('Failed to fetch list slots', e);
    } finally {
      setListLoading(false);
    }
  }, [token, planWeek]);

  useEffect(() => {
    if (planWeek.inPlanWeek) fetchListSlots();
  }, [fetchListSlots, planWeek.inPlanWeek]);

  // --- Guard: missing token ---
  if (!token) {
    const testToken = btoa('testtesttest');
    const testUrl = `${window.location.origin}/calendar/availability?token=${testToken}`;
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">Missing token. Please use the link provided to you.</Typography>
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Test URL (instructor_id=testtesttest):{' '}
          <a href={testUrl} target="_blank" rel="noopener noreferrer">{testUrl}</a>
        </Typography>
      </Box>
    );
  }

  // --- Guard: outside plan week ---
  if (!planWeek.inPlanWeek) {
    return (
      <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Availability Calendar{instructorName ? `: ${instructorName}` : ''}
          </Typography>
        </Paper>
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Plan week ended, please wait for the next plan week.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const allowedMonthStart = getAllowedMonthStart(planWeek.allowedYear, planWeek.allowedMonth);
  const allowedMonthEnd = getAllowedMonthEnd(planWeek.allowedYear, planWeek.allowedMonth);
  const allowedMonthEndExclusive = new Date(planWeek.allowedYear, planWeek.allowedMonth, 1);
  const allowedMonthEndExclusiveStr = formatLocalYmd(allowedMonthEndExclusive);
  const windowEndStr = formatPlanWindowDate(planWeek.windowEnd);
  const planMonthStr = formatPlanMonthName(planWeek.allowedYear, planWeek.allowedMonth);

  const isSubmitted = submissionStatus === 'YES';
  const isExpired = submissionStatus === 'EXPIRED';
  const isReadOnly = isSubmitted || isExpired;

  // --- Toggle a slot's local checked state ---
  const toggleSlotCheck = (slotKey) => {
    if (isReadOnly) return;
    setCheckedSlotIds((prev) => {
      const next = new Set(prev);
      if (next.has(slotKey)) next.delete(slotKey);
      else next.add(slotKey);
      return next;
    });
  };

  // --- Submit: confirm checked slots then mark overall submitted ---
  const performSubmit = async () => {
    if (!token) return;
    try {
      setSubmitting(true);
      const slotsToConfirm = listSlots.filter((s) => checkedSlotIds.has(getSlotInstanceKey(s)));
      if (slotsToConfirm.length > 0) {
        await Promise.all(
          slotsToConfirm.map((s) => {
            const date = normalizeCalendarDate(s.date);
            const st = String(s.startTime || '').trim().slice(0, 5);
            const et = String(s.endTime || '').trim().slice(0, 5);
            return axios.post(
              `${baseURL}/proposal/confirm/public`,
              {
                id: s.id,
                parentId: s.parentId || null,
                date,
                startTime: `${st}:00`,
                endTime: `${et}:00`,
              },
              { params: { token } },
            );
          }),
        );
      }
      await axios.post(`${baseURL}/proposal/submit/public`, null, { params: { token } });
      const nowPst = getNowInPst();
      setSubmissionStatus(getSubmissionStatus(nowPst.toISOString(), nowPst));
    } catch (e) {
      console.error('Submit failed', e);
      alert('Submit failed, please try again.');
    } finally {
      setSubmitting(false);
      setSubmitDialogOpen(false);
    }
  };

  // --- List view renderer ---
  const renderListView = () => {
    if (listLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (listSlots.length === 0) {
      return (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No proposed slots for {planMonthStr}.
          </Typography>
        </Paper>
      );
    }
    return (
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        <List disablePadding>
          {listSlots.map((slot, idx) => {
            const dateStr = normalizeCalendarDate(slot.date);
            const startStr = formatSlotTime(slot.startTime);
            const endStr = formatSlotTime(slot.endTime);
            const slotKey = getSlotInstanceKey(slot);
            const isChecked = checkedSlotIds.has(slotKey);
            const clickable = !isReadOnly;

            return (
              <React.Fragment key={`${slot.id}-${idx}`}>
                {idx > 0 && <Divider />}
                <ListItem
                  onClick={clickable ? () => toggleSlotCheck(slotKey) : undefined}
                  sx={{
                    cursor: clickable ? 'pointer' : 'default',
                    py: 1.5,
                    px: 2,
                    '&:hover': clickable ? { bgcolor: 'action.hover' } : {},
                    opacity: isExpired ? 0.6 : 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDateDisplay(dateStr)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {startStr} – {endStr}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isChecked ? (
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ color: 'action.disabled', fontSize: 28 }} />
                    )}
                  </Box>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>

      {/* Header */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {instructorName || 'Availability'}
        </Typography>

        {isSubmitted ? (
          <Typography variant="body1" color="success.main" fontWeight={700}>
            Submitted. No more actions required.
          </Typography>
        ) : (
          <>
            <Typography variant="body1" color="text.primary">
              Please confirm your availability for {planMonthStr}.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Click the slot to confirm this slot.
            </Typography>
            {countdown && (
              <Typography
                variant="body2"
                color="error"
                sx={{ mt: 1, fontWeight: 700 }}
              >
                {isExpired
                  ? `Expired at ${windowEndStr} 11:59pm`
                  : `Time left: ${countdown}`}
              </Typography>
            )}
          </>
        )}
      </Paper>

      {/* View toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => { if (v) setViewMode(v); }}
          size="small"
        >
          <ToggleButton value="list">
            <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
            List
          </ToggleButton>
          <ToggleButton value="calendar">
            <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5 }} />
            Calendar
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Main content */}
      {viewMode === 'list' ? (
        renderListView()
      ) : (
        <ProposalCalendar
          token={token}
          instructorAllowedMonthStart={allowedMonthStart}
          instructorAllowedMonthEnd={allowedMonthEnd}
          instructorValidRangeEnd={allowedMonthEndExclusiveStr}
          readOnly={isReadOnly}
        />
      )}

      {/* Submit button — bottom, only when not yet read-only */}
      {!isReadOnly && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => setSubmitDialogOpen(true)}
            sx={{ minWidth: 200 }}
          >
            SUBMIT
          </Button>
        </Box>
      )}

      {/* Submit confirmation dialog */}
      <Dialog
        open={submitDialogOpen}
        onClose={() => { if (!submitting) setSubmitDialogOpen(false); }}
      >
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {checkedSlotIds.size === 0
              ? 'You have not selected any slots. Submit anyway?'
              : `You are confirming ${checkedSlotIds.size} slot(s).`}
          </Typography>
          <Typography variant="body2" color="warning.main" fontWeight={600}>
            After submission, no further modification can be done.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={performSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ProposalCalendarPage;
